import {z} from 'zod';
import {checkValidURL} from '../../../shared/services/checkValidURL';
import {emailRegex} from '../../../shared/services/emailValidation';
import {OTHER} from '../reports/constants';
import {
  JOB_HUNTING_STATUS_RADIO,
  JOB_HUNTING_STATUS_RADIO_VALUE,
} from '../user/constants';
import {
  DevelopmentExperienceBodyLength,
  DevelopmentExperienceFormLength,
  DevelopmentServiceIdLength,
  EnglishSkillMaxPoints,
  MentalityLengthLimit,
  OfferStatusJoin,
  ReportStep,
} from './constants';

export const PutDmSettingsData = z.object({
  typeIds: z.array(z.number()),
});

export const PutScoutSettingsData = z.object({
  scoutFlg: z.boolean(),
  scoutTypeIDs: z.array(z.number()),
});

export const PostFolloyCompanyData = z.object({
  companyId: z.number(),
});
export const DeleteFolloyCompanyData = z.object({
  companyId: z.number(),
});

export const PutMyInterestsData = z.object({
  interestsId: z.number(),
  status: z.enum(['read', 'agreed']),
});

export const GetMeInterestsData = z.object({
  page: z.number(),
  limit: z.number(),
});

// GET /3.0/service/users/me/dm-settings
export const DmSetting = z.object({
  typeId: z.number(),
  frequency: z.string(),
  itemGroup: z.string(),
  stop: z.boolean(),
  uiDescription: z.string(),
  uiTitle: z.string(),
});

export const ScoutSetting = z.object({
  scoutSettingsTypeID: z.number(),
  stop: z.boolean(),
});

export const ScoutSettings = z.array(ScoutSetting);

export const PutDmSettingsResponse = z.number();
export const PutDmSettingsNonDisplayResponse = z.string();
export const PutScoutSettingsNonDisplayResponse = z.string();
export const DmSettingsResponse = z.array(DmSetting);
export const ScoutSettingsResponse = z.object({
  scoutFlg: z.boolean(),
  scoutStopSettings: ScoutSettings,
});
export const PostFollowCompanyResponse = z.object({
  followCompanyCount: z.number(),
});

export const DeleteFollowCompanyResponse = z.object({
  followCompanyCount: z.number(),
});

export const DeleteReportData = z.object({
  companyId: z.number(),
});

export const DeleteReportResponse = z.string();

// GET /3.0/service/users/me/general-profiles
export const REQUIRED_VALIDATION_MSG = '必須項目です';
export const MAIL_ADDRESS_VALIDATION_MSG =
  'メールアドレスの形式で入力してください';
export const MICROSOFT_MAIL_VALIDATION_MSG =
  '現在、Microsoftのメールアドレス（msn、hotmail、outlook、live）はご利用になれません';
export const GeneralProfileSchema = z.object({
  privateEmail: z
    .string()
    .regex(/@(?!(msn|hotmail|outlook|live))/, MICROSOFT_MAIL_VALIDATION_MSG)
    .regex(emailRegex, MAIL_ADDRESS_VALIDATION_MSG)
    .min(1, {message: REQUIRED_VALIDATION_MSG})
    .max(100, {message: '100文字以内で入力してください'}), // DB依存
  telNumber: z
    .string()
    .min(1, {message: REQUIRED_VALIDATION_MSG})
    .regex(/^[\d-]+$/, '半角数字とハイフンのみで入力してください'),
});

export const GeneralProfilesResponse = z.object({
  id: z.number(),
  familyName: z.string(),
  firstName: z.string(),
  yomiFamilyName: z.string(),
  yomiFirstName: z.string(),
  alphabetFamilyName: z.string(),
  alphabetFirstName: z.string(),
  sex: z.number(),
  birthday: z.string(),
  telNumber: z.string(),
  privateEmail: z.string(),
  acEmail: z.string(),
  modified: z.string(),
});

export const PutGeneralProfilesResponse = z.object({
  telNumber: z.string(),
  privateEmail: z.string(),
  modified: z.string(),
});

// GET /3.0/mypage/profile/edit/pr_message
export const PrMessagesSchema = z.object({
  onelineMessage1: z
    .string()
    .max(20, {message: 'ひとことPRは20文字以内で入力してください'}),
  onelineMessage2: z
    .string()
    .max(20, {message: 'ひとことPRは20文字以内で入力してください'}),
  onelineMessage3: z
    .string()
    .max(20, {message: 'ひとことPRは20文字以内で入力してください'}),
  message: z.string(),
});

export const PrMessagesResponse = z.object({
  id: z.number(),
  userID: z.number(),
  onelineMessage1: z.string(),
  onelineMessage2: z.string(),
  onelineMessage3: z.string(),
  message: z.string(),
  modified: z.string(),
  created: z.string(),
});

// PUT /3.0/mypage/profile/edit/pr_message
export const PutPrMessagesResponse = z.object({
  onelineMessage1: z.string(),
  onelineMessage2: z.string(),
  onelineMessage3: z.string(),
  message: z.string(),
});

// GET /3.0/mypage/profile/edit/wish
export const WishFormSchema = z.object({
  locations: z.object({
    location1: z.string(),
    location2: z.string(),
    location3: z.string(),
  }),
  jobs: z.object({
    job1: z.string(),
    job2: z.string(),
    job3: z.string(),
  }),
  industries: z.object({
    industry1: z.string(),
    industry2: z.string(),
    industry3: z.string(),
  }),
  companies: z.object({
    company1: z.object({
      companyName: z.string(),
      industryID: z.number(),
      wishID: z.number(),
    }),
    company2: z.object({
      companyName: z.string(),
      industryID: z.number(),
      wishID: z.number(),
    }),
    company3: z.object({
      companyName: z.string(),
      industryID: z.number(),
      wishID: z.number(),
    }),
  }),
});

export const LocationMasterSchema = z.object({
  id: z.number(),
  name: z.string(),
  isForeign: z.boolean(),
});

export const LocationMasterResponse = z.object({
  locations: z.array(LocationMasterSchema),
});

const WishSchema = z.object({
  wishID: z.number(),
  wishOrder: z.number(),
});

export const WishResponse = z.object({
  locations: z.array(WishSchema.extend({locationName: z.string()})),
  jobs: z.array(WishSchema.extend({jobTypeName: z.string()})),
  industries: z.array(WishSchema.extend({industryName: z.string()})),
  companies: z.array(
    WishSchema.extend({
      companyName: z.string(),
      industryID: z.number(),
    }),
  ),
});

export const PutWishesResponse = z.object({
  locations: z.array(
    z.object({
      wishID: z.number(),
      wishOrder: z.number(),
      type: z.string(),
    }),
  ),
  jobs: z.array(
    z.object({
      wishID: z.number(),
      wishOrder: z.number(),
      type: z.string(),
    }),
  ),
  industries: z.array(
    z.object({
      wishID: z.number(),
      wishOrder: z.number(),
      type: z.string(),
    }),
  ),
  companies: z.array(
    z.object({
      wishID: z.number(),
      wishOrder: z.number(),
      type: z.string(),
      industryID: z.number(),
      companyName: z.string(),
    }),
  ),
});

export const PutWishesSchema = z.object({
  locations: z.array(
    z.object({
      wishID: z.number(),
      wishOrder: z.number(),
      type: z.string(),
    }),
  ),
  jobs: z.array(
    z.object({
      wishID: z.number(),
      wishOrder: z.number(),
      type: z.string(),
    }),
  ),
  industries: z.array(
    z.object({
      wishID: z.number(),
      wishOrder: z.number(),
      type: z.string(),
    }),
  ),
  companies: z.array(
    z.object({
      wishID: z.number(),
      wishOrder: z.number(),
      type: z.string(),
      industryID: z.number(),
      companyName: z.string(),
    }),
  ),
});

export const JobMasterSchema = z.object({
  id: z.number(),
  name: z.string(),
  category: z.string(),
});

export const JobMasterResponse = z.object({
  jobTypes: z.array(JobMasterSchema),
});

export const IndustryMasterSchema = z.object({
  id: z.number(),
  name: z.string(),
  shortName: z.string(),
});

export const IndustryMasterResponse = z.object({
  industries: z.array(IndustryMasterSchema),
});

export const CompanyMasterSchema = z.object({
  industryID: z.number(),
  industryName: z.string(),
  companyList: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    }),
  ),
});

export const CompanyMasterResponse = z.object({
  groupedCompanies: z.array(CompanyMasterSchema),
});

export const MentalitiesSchema = z.object({
  userIntention: z
    .array(z.number())
    .refine(
      (data) =>
        data.length >= MentalityLengthLimit.MIN &&
        data.length <= MentalityLengthLimit.MAX,
      {
        message: `${MentalityLengthLimit.MIN}つ〜${MentalityLengthLimit.MAX}つまでの間で選択してください。`,
      },
    ),
  userPersonality: z
    .array(z.number())
    .refine((data) => data.length <= MentalityLengthLimit.MAX, {
      message: `選択できるのは${MentalityLengthLimit.MAX}つまでです。`,
    }),
});

export const MentalityMasterSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const IntentionMasterResponse = z.object({
  intentions: z.array(MentalityMasterSchema),
});

export const PersonalityMasterResponse = z.object({
  personalities: z.array(MentalityMasterSchema),
});

export const MentalityResponse = z.object({
  userIntention: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    }),
  ),
  userPersonality: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    }),
  ),
});

export const PutMentalitiesResponse = z.object({
  userIntention: z.array(z.number()),
  userPersonality: z.array(z.number()),
});

const UniversityCommonProperties = z
  .object({
    major: z.string().min(1, {message: '必須項目です'}),
    subject: z.string().min(1, {message: '必須項目です'}),
    bunRi: z.string().min(1, {message: '必須項目です'}),
    enrollYear: z.string().min(1, {message: '必須項目です'}),
    enrollMonth: z.string().min(1, {message: '必須項目です'}),
    graduateYear: z.string().min(1, {message: '必須項目です'}),
    graduateMonth: z.string().min(1, {message: '必須項目です'}),
    laboName: z.string(),
    laboID: z.number(),
    laboTheme: z.string(),
    professor: z.string(),
    grade: z.string().optional(),
  })
  .refine(
    (obj) => {
      const enrollDate = Number(
        `${obj.enrollYear}${obj.enrollMonth.padStart(2, '0')}`,
      );
      const graduateDate = Number(
        `${obj.graduateYear}${obj.graduateMonth.padStart(2, '0')}`,
      );

      return enrollDate <= graduateDate;
    },
    {message: '在籍期間に誤りがあります。'},
  );

export const EducationsSchema = z.object({
  highschool: z
    .object({
      enrollYear: z.string().min(1, {message: '必須項目です'}),
      enrollMonth: z.string().min(1, {message: '必須項目です'}),
      graduateYear: z.string().min(1, {message: '必須項目です'}),
      graduateMonth: z.string().min(1, {message: '必須項目です'}),
      name: z.string().min(1, {message: '必須項目です'}),
    })
    .refine(
      (obj) => {
        const enrollDate = Number(
          `${obj.enrollYear}${obj.enrollMonth.padStart(2, '0')}`,
        );
        const graduateDate = Number(
          `${obj.graduateYear}${obj.graduateMonth.padStart(2, '0')}`,
        );

        return enrollDate <= graduateDate;
      },
      {message: '在籍期間に誤りがあります。'},
    ),
  associate: UniversityCommonProperties.optional(),
  bachelor: UniversityCommonProperties.optional(),
  graduate: UniversityCommonProperties.optional(),
  doctor: UniversityCommonProperties.optional(),
});

export const EducationResponse = z.object({
  highschool: z.object({
    enrollYear: z.number(),
    enrollMonth: z.number(),
    graduateYear: z.number(),
    graduateMonth: z.number(),
    name: z.string(),
  }),
  associate: z
    .object({
      majorID: z.number(),
      majorName: z.string(),
      subjectID: z.number(),
      subjectName: z.string(),
      enrollYear: z.number(),
      enrollMonth: z.number(),
      graduateYear: z.number(),
      graduateMonth: z.number(),
      universityID: z.number(),
      universityName: z.string(),
      universityCategory: z.string(),
      bunRi: z.number(),
      laboName: z.string(),
      laboID: z.number(),
      laboTheme: z.string(),
      professorID: z.number(),
      professorName: z.string(),
      grade: z.number().optional(),
    })
    .optional(),
  bachelor: z
    .object({
      majorID: z.number(),
      majorName: z.string(),
      subjectID: z.number(),
      subjectName: z.string(),
      enrollYear: z.number(),
      enrollMonth: z.number(),
      graduateYear: z.number(),
      graduateMonth: z.number(),
      universityID: z.number(),
      universityName: z.string(),
      universityCategory: z.string(),
      bunRi: z.number(),
      laboName: z.string(),
      laboID: z.number(),
      laboTheme: z.string(),
      professorID: z.number(),
      professorName: z.string(),
      grade: z.number().optional(),
    })
    .optional(),
  graduate: z
    .object({
      majorID: z.number(),
      majorName: z.string(),
      subjectID: z.number(),
      subjectName: z.string(),
      enrollYear: z.number(),
      enrollMonth: z.number(),
      graduateYear: z.number(),
      graduateMonth: z.number(),
      universityID: z.number(),
      universityName: z.string(),
      universityCategory: z.string(),
      bunRi: z.number(),
      laboName: z.string(),
      laboID: z.number(),
      laboTheme: z.string(),
      professorID: z.number(),
      professorName: z.string(),
      grade: z.number().optional(),
    })
    .optional(),
  doctor: z
    .object({
      majorID: z.number(),
      majorName: z.string(),
      subjectID: z.number(),
      subjectName: z.string(),
      enrollYear: z.number(),
      enrollMonth: z.number(),
      graduateYear: z.number(),
      graduateMonth: z.number(),
      universityID: z.number(),
      universityName: z.string(),
      universityCategory: z.string(),
      bunRi: z.number(),
      laboName: z.string(),
      laboID: z.number(),
      laboTheme: z.string(),
      professorID: z.number(),
      professorName: z.string(),
      grade: z.number().optional(),
    })
    .optional(),
});

export const PutEducationsSchema = z.object({
  highschool: z.object({
    enrollYear: z.number(),
    enrollMonth: z.number(),
    graduateYear: z.number(),
    graduateMonth: z.number(),
    name: z.string(),
  }),
  associate: z
    .object({
      major: z.string(),
      subject: z.string(),
      bunRi: z.number(),
      enrollYear: z.number(),
      enrollMonth: z.number(),
      graduateYear: z.number(),
      graduateMonth: z.number(),
      laboName: z.string(),
      laboTheme: z.string(),
      professor: z.string(),
      grade: z.number().optional(),
    })
    .optional(),
  bachelor: z
    .object({
      major: z.string(),
      subject: z.string(),
      bunRi: z.number(),
      enrollYear: z.number(),
      enrollMonth: z.number(),
      graduateYear: z.number(),
      graduateMonth: z.number(),
      laboName: z.string(),
      laboTheme: z.string(),
      professor: z.string(),
      grade: z.number().optional(),
    })
    .optional(),
  graduate: z
    .object({
      major: z.string(),
      subject: z.string(),
      bunRi: z.number(),
      enrollYear: z.number(),
      enrollMonth: z.number(),
      graduateYear: z.number(),
      graduateMonth: z.number(),
      laboName: z.string(),
      laboID: z.number(),
      laboTheme: z.string(),
      professor: z.string(),
      grade: z.number().optional(),
    })
    .optional(),
  doctor: z
    .object({
      major: z.string(),
      subject: z.string(),
      bunRi: z.number(),
      enrollYear: z.number(),
      enrollMonth: z.number(),
      graduateYear: z.number(),
      graduateMonth: z.number(),
      laboName: z.string(),
      laboID: z.number(),
      laboTheme: z.string(),
      professor: z.string(),
      grade: z.number().optional(),
    })
    .optional(),
});

export const PutEducationsResponse = z.object({
  highschool: z.object({
    enrollYear: z.number(),
    enrollMonth: z.number(),
    graduateYear: z.number(),
    graduateMonth: z.number(),
    name: z.string(),
  }),
  associate: z
    .object({
      major: z.string(),
      subject: z.string(),
      bunRi: z.number(),
      enrollYear: z.number(),
      enrollMonth: z.number(),
      graduateYear: z.number(),
      graduateMonth: z.number(),
      laboName: z.string(),
      laboTheme: z.string(),
      professor: z.string(),
    })
    .optional(),
  bachelor: z
    .object({
      major: z.string(),
      subject: z.string(),
      bunRi: z.number(),
      enrollYear: z.number(),
      enrollMonth: z.number(),
      graduateYear: z.number(),
      graduateMonth: z.number(),
      laboName: z.string(),
      laboTheme: z.string(),
      professor: z.string(),
    })
    .optional(),
  graduate: z
    .object({
      major: z.string(),
      subject: z.string(),
      bunRi: z.number(),
      enrollYear: z.number(),
      enrollMonth: z.number(),
      graduateYear: z.number(),
      graduateMonth: z.number(),
      laboName: z.string(),
      laboTheme: z.string(),
      professor: z.string(),
    })
    .optional(),
  doctor: z
    .object({
      major: z.string(),
      subject: z.string(),
      bunRi: z.number(),
      enrollYear: z.number(),
      enrollMonth: z.number(),
      graduateYear: z.number(),
      graduateMonth: z.number(),
      laboName: z.string(),
      laboTheme: z.string(),
      professor: z.string(),
    })
    .optional(),
});

export const InternsSchema = z.object({
  interns: z.array(
    z
      .object({
        company: z.object({
          companyID: z.number(),
          companyName: z.string(),
          industryID: z.number(),
        }),
        departmentName: z.string(),
        jobTypeID: z.string(),
        termTypeID: z.string(),
      })
      .refine(
        (data) => {
          if (
            data.jobTypeID === '0' &&
            data.termTypeID === '0' &&
            data.departmentName === ''
          ) {
            return true;
          }

          return data.company.companyName !== '';
        },
        {
          message: '企業名を選択・入力してください',
          path: ['companyNameRequired'],
        },
      )
      .refine(
        (data) => {
          if (
            data.company.companyName === '' &&
            data.termTypeID === '0' &&
            data.departmentName === ''
          ) {
            return true;
          }

          return data.jobTypeID !== '' && data.jobTypeID !== '0';
        },
        {
          message: '職種を選択してください',
          path: ['jobTypeIDRequired'],
        },
      )
      .refine(
        (data) => {
          if (
            data.jobTypeID === '0' &&
            data.company.companyName === '' &&
            data.departmentName === ''
          ) {
            return true;
          }

          return data.termTypeID !== '' && data.termTypeID !== '0';
        },
        {
          message: '期間を選択してください',
          path: ['termTypeIDRequired'],
        },
      ),
  ),
});

export const InternTermMasterSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const InternTermMasterResponse = z.object({
  internTerms: z.array(InternTermMasterSchema),
});

export const InternResponse = z.object({
  interns: z.array(
    z.object({
      company: z.object({
        companyID: z.number(),
        companyName: z.string(),
        industryID: z.number(),
      }),
      departmentName: z.string(),
      jobType: z.object({id: z.number(), name: z.string()}),
      termType: z.object({id: z.number(), name: z.string()}),
    }),
  ),
});

export const PutInternsResponse = z.object({
  interns: z.array(
    z.object({
      company: z.object({
        companyID: z.number(),
        companyName: z.string(),
        industryID: z.number(),
      }),
      departmentName: z.string(),
      jobTypeID: z.number(),
      termTypeID: z.number(),
    }),
  ),
});

export const PutInternsSchema = z.object({
  interns: z.array(
    z.object({
      company: z.object({
        companyID: z.number(),
        companyName: z.string(),
        industryID: z.number(),
      }),
      departmentName: z.string(),
      jobTypeID: z.number(),
      termTypeID: z.number(),
    }),
  ),
});

export const AdditionalBackgroundsSchema = z.object({
  additionalBackgrounds: z.array(z.number()),
});

export const AdditionalBackgroundMasterSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const AdditionalBackgroundMasterResponse = z.object({
  additionalBackgrounds: z.array(AdditionalBackgroundMasterSchema),
});

export const AdditionalBackgroundResponse = z.object({
  additionalBackgrounds: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    }),
  ),
});

export const PutAdditionalBackgroundsResponse = z.object({
  additionalBackgrounds: z.array(z.number()),
});

export const LanguagesSchema = z
  .object({
    toeic: z
      .number()
      .max(EnglishSkillMaxPoints.TOEIC, {
        message: `${EnglishSkillMaxPoints.TOEIC}点以下で入力してください`,
      })
      .min(0, {message: '0点以上で入力してください'}),
    toeflType: z.string(),
    toeflPoint: z.number().min(0, {message: '0点以上で入力してください'}),
    languageLevel: z.array(
      z
        .object({
          languageID: z.string(),
          levelID: z.string(),
        })
        .refine(
          (data) => {
            if (data.languageID !== '0') {
              return data.levelID !== '0';
            }

            return true;
          },
          {
            message: 'レベルを選択してください',
            path: ['levelIDRequired'],
          },
        )
        .refine(
          (data) => {
            if (data.levelID !== '0') {
              return data.languageID !== '0';
            }

            return true;
          },
          {
            message: '言語を選択してください',
            path: ['languageIDRequired'],
          },
        ),
    ),
  })
  .refine(
    (data) => {
      if (data.toeflPoint !== 0) {
        return data.toeflType !== '';
      }

      return true;
    },
    {message: '種類を選択してください', path: ['toeflPoint']},
  )
  .refine(
    (data) => {
      if (data.toeflType !== '') {
        return data.toeflPoint !== 0;
      }

      return true;
    },
    {message: '点数を入力してください', path: ['toeflPoint']},
  )
  .refine(
    (data) => {
      if (data.toeflType === 'iBT') {
        return data.toeflPoint <= EnglishSkillMaxPoints.TOEFL_IBT;
      }

      return true;
    },
    {
      message: `iBTのスコアは${EnglishSkillMaxPoints.TOEFL_IBT}点以下で入力してください`,
      path: ['toeflPoint'],
    },
  )
  .refine(
    (data) => {
      if (data.toeflType === 'ITP') {
        return data.toeflPoint <= EnglishSkillMaxPoints.TOEFL_ITP;
      }

      return true;
    },
    {
      message: `ITPのスコアは${EnglishSkillMaxPoints.TOEFL_ITP}点以下で入力してください`,
      path: ['toeflPoint'],
    },
  );

export const LanguageMasterSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const LanguageMasterResponse = z.object({
  languages: z.array(LanguageMasterSchema),
});

export const LanguageResponse = z.object({
  toeic: z.number(),
  toeflType: z.string(),
  toeflPoint: z.number(),
  languageLevel: z.array(
    z.object({
      languageID: z.number(),
      languageName: z.string(),
      levelID: z.number(),
      levelName: z.string(),
    }),
  ),
});

export const HashTagSchema = z.object({
  hashtagID: z.number(),
  hashtagName: z.string().nullable(),
});

export const DevelopmentExperienceSchema = z
  .object({
    id: z.number().optional(),
    body: z.string().max(DevelopmentExperienceBodyLength, {
      message: `最大${DevelopmentExperienceBodyLength}文字までです`,
    }),
    categoryTag: HashTagSchema,
    programmingLanguageTags: z.array(HashTagSchema),
    libraryFrameworkTags: z.array(HashTagSchema),
    otherTags: z.array(HashTagSchema),
    startDate: z.string(),
    endDate: z.string(),
  })
  .refine(
    (schema) => {
      // 開発経験内の項目が全て空の場合はOK
      if (
        schema.body.length === 0 &&
        schema.categoryTag.hashtagID === 0 &&
        schema.programmingLanguageTags.length === 0 &&
        schema.libraryFrameworkTags.length === 0 &&
        schema.otherTags.length === 0 &&
        !schema.startDate &&
        !schema.endDate
      ) {
        return true;
      }

      // 以下の場合はエラー
      // カテゴリ・開発経験の内容・期間が空
      // 「開発言語」「ライブラリ、フレームワーク」「その他」のいずれも入力されていない
      return (
        schema.body.length > 0 &&
        schema.categoryTag.hashtagID > 0 &&
        schema.startDate &&
        (schema.programmingLanguageTags.length > 0 ||
          schema.libraryFrameworkTags.length > 0 ||
          schema.otherTags.length > 0)
      );
    },
    {
      message:
        'カテゴリ・開発経験の内容・期間は必須項目です。また開発言語、ライブラリ・フレームワーク、その他のいずれかを入力してください',
    },
  )
  .refine(
    (schema) => {
      if (!schema.startDate || !schema.endDate) return true;
      const startDate = new Date(schema.startDate);
      const endDate = new Date(schema.endDate);
      return startDate <= endDate;
    },
    {message: '開発事例の終了日は開始日より後にしてください'},
  );

export const ItSkillsSchema = z.object({
  experiences: z
    .array(DevelopmentExperienceSchema)
    .max(DevelopmentExperienceFormLength, {
      message: `最大${DevelopmentExperienceFormLength}つまでしか登録できません`,
    }),
  service: z.object({
    githubID: z.string().max(DevelopmentServiceIdLength),
    qiitaID: z.string().max(DevelopmentServiceIdLength),
    atCoderID: z.string().max(DevelopmentServiceIdLength),
    kaggleID: z.string().max(DevelopmentServiceIdLength),
    mySites: z.array(
      z.string().refine((value) => value === '' || checkValidURL(value)),
    ),
    publishLevel: z.number(),
    hasEngineerInterest: z
      .boolean()
      .optional()
      .refine((val) => val !== undefined, {
        message: '必須項目です',
      })
      .transform((val) => (val === undefined ? undefined : val)),
    summaryTags: z.array(HashTagSchema).nullable(),
  }),
});

export const PutDevelopmentExperienceSchema = z.object({
  id: z.number().optional(),
  body: z.string(),
  categoryTagID: z.number().optional(),
  hashtagIDs: z.array(z.number()),
  startYear: z.number().optional(),
  startMonth: z.number().optional(),
  endYear: z.number().optional(),
  endMonth: z.number().optional(),
});

export const PutItSkillsSchema = z.object({
  experiences: z.array(PutDevelopmentExperienceSchema),
  service: z.object({
    githubID: z.string(),
    qiitaID: z.string(),
    atCoderID: z.string(),
    kaggleID: z.string(),
    mySites: z.array(z.string()),
    publishLevel: z.number(),
    hasEngineerInterest: z.boolean().optional(),
    summaryTagIDs: z.array(z.number()).nullable(),
  }),
});

export const DevelopmentExperienceResponse = z.object({
  developmentExperiences: z.array(
    z.object({
      id: z.number(),
      body: z.string(),
      categoryTag: HashTagSchema.nullable(),
      programmingLanguageTags: z.array(HashTagSchema),
      libraryFrameworkTags: z.array(HashTagSchema),
      otherTags: z.array(HashTagSchema),
      startYear: z.number().nullable(),
      startMonth: z.number().nullable(),
      endYear: z.number().nullable(),
      endMonth: z.number().nullable(),
    }),
  ),
});

export const PutLanguagesSchema = z.object({
  toeic: z.number(),
  toeflType: z.string(),
  toeflPoint: z.number(),
  languageLevel: z.array(
    z.object({
      languageID: z.number(),
      levelID: z.number(),
    }),
  ),
});

export const PutLanguagesResponse = z.object({
  toeic: z.number().optional(),
  toeflType: z.string(),
  toeflPoint: z.number(),
  languageLevel: z.array(
    z.object({
      languageID: z.number(),
      levelID: z.number(),
    }),
  ),
});

export const DevelopmentServiceResponse = z.object({
  developmentService: z.object({
    id: z.number(),
    githubID: z.string(),
    qiitaID: z.string(),
    atCoderID: z.string(),
    atCoderRating: z.number(),
    kaggleID: z.string(),
    kaggleRank: z.string(),
    mySites: z.array(z.string()),
    publishLevel: z.number(),
    hasEngineerInterest: z.boolean().nullable(),
    summaryTags: z.array(HashTagSchema),
  }),
});

export const DeleteDevelopmentExperienceResponse = z.string();

export const PutDevelopmentExperienceResponse = z.object({
  developmentExperience: z.object({
    id: z.number(),
    body: z.string(),
    hashtags: z
      .array(
        z.object({
          id: z.number(),
          name: z.string(),
          usedCount: z.number(),
        }),
      )
      .nullable(),
  }),
});

export const PutDevelopmentServiceResponse = z.object({
  DevelopmentService: z.object({
    id: z.number(),
    githubID: z.string(),
    qiitaID: z.string(),
    atCoderID: z.string(),
    atCoderRating: z.number(),
    atCoderColor: z.string(),
    kaggleID: z.string(),
    kaggleRank: z.string(),
    mySites: z.array(z.string()),
    publishLevel: z.number(),
    hasEngineerInterest: z.boolean().nullable(),
    summaryTags: z.array(HashTagSchema).nullable(),
  }),
});

export const PostDevelopmentExperienceResponse = z.object({});

export const PutItSkillResponse = z.string();

export const HashtagListResponse = z.object({
  hashtags: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      usedCount: z.number(),
    }),
  ),
});

const STATUS_TYPE_LIST = ['unread', 'read', 'agreed', 'unknown'] as const;

export const GetMeInterestsResponse = z.object({
  interests: z.array(
    z.object({
      id: z.number(),
      body: z.string(),
      companyID: z.number(),
      companyName: z.string(),
      createdAt: z.string(),
      imageURL: z.string(),
      industryName: z.string(),
      status: z.enum(STATUS_TYPE_LIST),
      isFollowed: z.boolean(),
    }),
  ),
  nextPage: z.number(),
  totalCount: z.number(),
});

export const PutMyInterestsResponse = z.string();

export const ForeignExperiencesSchema = z.object({
  foreignExperiences: z.array(
    z
      .object({
        countryID: z.string(),
        startYear: z.string(),
        startMonth: z.string(),
        endYear: z.string(),
        endMonth: z.string(),
      })
      .refine(
        (data) => {
          if (
            (data.startYear !== '' ||
              data.startMonth !== '' ||
              data.endYear !== '' ||
              data.endMonth !== '') &&
            data.countryID === ''
          ) {
            return false;
          }

          return true;
        },
        {
          message: '滞在した国名を入力してください。',
          path: ['countryIDRequired'],
        },
      )
      .refine(
        (data) => {
          if (
            (data.countryID !== '' ||
              data.startMonth !== '' ||
              data.endYear !== '' ||
              data.endMonth !== '') &&
            data.startYear === ''
          ) {
            return false;
          }

          return true;
        },
        {
          message: '滞在開始年を入力してください。',
          path: ['startYearRequired'],
        },
      )
      .refine(
        (data) => {
          if (
            (data.countryID !== '' ||
              data.startYear !== '' ||
              data.endYear !== '' ||
              data.endMonth !== '') &&
            data.startMonth === ''
          ) {
            return false;
          }

          return true;
        },
        {
          message: '滞在開始月を入力してください。',
          path: ['startMonthRequired'],
        },
      )
      .refine(
        (data) => {
          if (
            (data.countryID !== '' ||
              data.startYear !== '' ||
              data.startMonth !== '' ||
              data.endMonth !== '') &&
            data.endYear === ''
          ) {
            return false;
          }

          return true;
        },
        {
          message: '滞在終了年を入力してください。',
          path: ['endYearRequired'],
        },
      )
      .refine(
        (data) => {
          if (
            (data.countryID !== '' ||
              data.startYear !== '' ||
              data.startMonth !== '' ||
              data.endYear !== '') &&
            data.endMonth === ''
          ) {
            return false;
          }

          return true;
        },
        {
          message: '滞在終了月を入力してください。',
          path: ['endMonthRequired'],
        },
      )
      .refine(
        (data) => {
          if (
            data.startYear === '' ||
            data.startMonth === '' ||
            data.endYear === '' ||
            data.endMonth === ''
          ) {
            return true;
          }

          const startDate = Number(
            `${data.startYear}${data.startMonth.padStart(2, '0')}`,
          );
          const endDate = Number(
            `${data.endYear}${data.endMonth.padStart(2, '0')}`,
          );

          return startDate <= endDate;
        },
        {
          message: '終了年月を開始年月より未来の年月にしてください。',
          path: ['startEndDate'],
        },
      ),
  ),
});

export const CountryMasterResponse = z.object({
  countries: z.array(
    z.object({
      area: z.string(),
      countryList: z.array(
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      ),
    }),
  ),
});

export const ForeignExperienceResponse = z.object({
  foreignExperiences: z.array(
    z.object({
      countryID: z.number(),
      countryName: z.string(),
      startYear: z.number(),
      startMonth: z.number(),
      endYear: z.number(),
      endMonth: z.number(),
    }),
  ),
});

export const FormForeignExperiencesSchema = z.object({
  foreignExperiences: z.array(
    z.object({
      countryID: z.string(),
      startYear: z.string(),
      startMonth: z.string(),
      endYear: z.string(),
      endMonth: z.string(),
    }),
  ),
});

export const PutForeignExperiencesSchema = z.object({
  foreignExperiences: z.array(
    z.object({
      countryID: z.number(),
      startYear: z.number(),
      startMonth: z.number(),
      endYear: z.number(),
      endMonth: z.number(),
    }),
  ),
});

export const PutForeignExperiencesResponse = z.object({
  foreignExperiences: z.array(
    z.object({
      countryID: z.number(),
      startYear: z.number(),
      startMonth: z.number(),
      endYear: z.number(),
      endMonth: z.number(),
    }),
  ),
});

export const JobHuntingStatusRadioSchema = z.enum([
  ...JOB_HUNTING_STATUS_RADIO_VALUE,
  '',
]);

export const JobHuntingStatusAndOffersFormSchema = z
  .object({
    offers: z.array(
      z
        .object({
          company: z.object({
            companyID: z.number(),
            companyName: z.string(),
            industryID: z.number(),
          }),
          departmentName: z.string(),
          jobTypeID: z.string(),
          status: z.string(),
          reason: z.string(),
        })
        .refine(
          (data) => {
            if (
              (data.jobTypeID === '0' || data.jobTypeID === '') &&
              data.departmentName === ''
            ) {
              return true;
            }

            return data.company.companyName !== '';
          },
          {
            message: '企業名を選択・入力してください',
            path: ['companyNameRequired'],
          },
        )
        .refine(
          (data) => {
            if (data.company.companyName === '' && data.departmentName === '') {
              return true;
            }

            return data.jobTypeID !== '' && data.jobTypeID !== '0';
          },
          {
            message: '職種を選択してください',
            path: ['jobTypeIDRequired'],
          },
        ),
    ),
    for: z.string(),
    jobHuntingStatus: JobHuntingStatusRadioSchema,
  })
  .refine(
    (data) => {
      // 「入社する」企業が2つ以上選択されている場合はバリデーションエラー
      const statusCount = data.offers.filter(
        (offer) => offer.status === OfferStatusJoin,
      ).length;
      return statusCount < 2;
    },
    {
      message: '入社先は1社のみ選択してください。',
      path: ['statusValidation'],
    },
  )
  .refine(
    (data) => {
      // For="liiga" or "offered"の場合、内定先が必須
      if (data.for === 'liiga' || data.for === 'offered') {
        return data.offers.some((offer) => {
          return (
            (offer.company.companyName !== '' &&
              data.jobHuntingStatus ===
                JOB_HUNTING_STATUS_RADIO.doing_has_offer) ||
            data.jobHuntingStatus === JOB_HUNTING_STATUS_RADIO.after
          );
        });
      }

      return true;
    },
    {
      message: '内定先の入力が必須です。',
      path: ['offeredRequired'],
    },
  )
  .refine(
    (data) => {
      // For="offered"の場合とjobHuntingStatus=afterの場合、入社先が必須
      if (
        data.for === 'offered' ||
        data.jobHuntingStatus === JOB_HUNTING_STATUS_RADIO.after
      ) {
        return data.offers.some((offer) => offer.status === OfferStatusJoin);
      }

      return true;
    },
    {
      message: '「入社する」内定先が必須です。',
      path: ['joinRequired'],
    },
  )
  .refine(
    (data) => {
      return data.jobHuntingStatus !== '';
    },
    {
      message: '就活状況を選択してください。',
      path: ['jobHuntingStatusRequired'],
    },
  )
  .refine(
    (data) => {
      // 「就活状況」が「既に内定はあるが就活継続中」または「就活を既に終了している」の場合、内定先が必須
      if (
        data.jobHuntingStatus === JOB_HUNTING_STATUS_RADIO.doing_has_offer ||
        data.jobHuntingStatus === JOB_HUNTING_STATUS_RADIO.after
      ) {
        return data.offers.some((offer) => {
          return offer.company.companyName !== '';
        });
      }

      return true;
    },
    {
      message: '内定先の入力が必須です。',
      path: ['offeredRequired'],
    },
  );

export const OffersResponse = z.object({
  offers: z.array(
    z.object({
      company: z.object({
        companyID: z.number(),
        companyName: z.string(),
        industryID: z.number(),
      }),
      departmentName: z.string(),
      jobType: z.object({id: z.number(), name: z.string()}),
      status: z.number(),
      reason: z.string(),
    }),
  ),
});

export const PutOffersSchema = z.object({
  offers: z.array(
    z.object({
      company: z.object({
        companyID: z.number(),
        companyName: z.string(),
        industryID: z.number(),
      }),
      departmentName: z.string(),
      jobTypeID: z.number(),
      status: z.number(),
      reason: z.string(),
    }),
  ),
  for: z.string(),
});

export const PutOffersResponse = z.object({
  offers: z.array(
    z.object({
      company: z.object({
        companyID: z.number(),
        companyName: z.string(),
        industryID: z.number(),
      }),
      departmentName: z.string(),
      jobTypeID: z.number(),
      status: z.number(),
      reason: z.string(),
    }),
  ),
});

// POST /3.0/
export const RepostsEsNewResponse = z.object({
  reportType: z.string().min(1, {message: '必須項目です'}),
  companyID: z.string().min(1, {message: '必須項目です'}).transform(Number),
  eventName: z.string(),
});

export const PostEmailAuthResponse = z.null();

// POST /3.0/selection-reports
export const RepostsAddResponse = z.object({
  reportType: z.string().min(1, {message: '必須項目です'}),
  companyID: z.string().min(1, {message: '必須項目です'}).transform(Number),
  eventName: z.string(),
});

export const PostAddResponse = z.object({
  reportType: z.string(),
  companyID: z.number(),
  eventName: z.string(),
});

const ReportInfoSchema = z.object({
  industryID: z.number(),
  industryName: z.string(),
  companyList: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    }),
  ),
});

// GET /3.0/mypage/reports/reportable-companies
export const GetReportsAddSelectionListResponse = z.array(ReportInfoSchema);
export const GetReportsAddInternListResponse = z.array(ReportInfoSchema);

export const RepostsEditResponse = z
  .object({
    department: z
      .string()
      .optional()
      .refine((data) => data !== '0', {
        message: '必須項目です',
      }),
    another: z
      .string()
      .optional()
      .refine((data) => data !== '', {
        message: '入力して下さい。',
      }),
    selectionSeason: z
      .string()
      .optional()
      .refine((data) => data !== '', {
        message: '必須項目です',
      }),

    reportStep: z
      .string()
      .optional()
      .refine((data) => data !== '', {
        message: '必須項目です',
      }),
    // オプショナルなので、空文字列の場合はエラーにならない
    // nullを許容する場合は.optional()を使う

    hasPreference: z
      .string()
      .optional()
      .refine((data) => data !== '', {
        message: '入力して下さい。',
      }),
    preferenceDetail: z.string().optional(),

    hasVisitedOb: z
      .string()
      .optional()
      .refine((data) => data !== '', {
        message: '入力して下さい。',
      }),
    hasReceivedOffer: z.optional(
      z.string().min(1, {message: '入力して下さい。'}),
    ),
    internshipSchedule: z.optional(
      z.string().min(1, {message: '入力して下さい。'}),
    ),
    internshipHowlong: z.optional(
      z.string().min(1, {message: '入力して下さい。'}),
    ),
    internshipPlace: z
      .string()
      .optional()
      .refine((data) => data !== '', {
        message: '入力して下さい。',
      }),
    internshipReward: z.optional(
      z.string().min(1, {message: '入力して下さい。'}),
    ),
    internshipCollege: z.optional(
      z.string().min(1, {message: '入力して下さい。'}),
    ),
    internshipTheme: z
      .string()
      .optional()
      .refine((data) => data !== '', {
        message: '入力して下さい。',
      }),
    internshipProcedure: z.optional(
      z.string().min(1, {message: '入力して下さい。'}),
    ),
    internshipAtmosphere: z.optional(
      z.string().min(1, {message: '入力して下さい。'}),
    ),
    internshipMentor: z.optional(
      z.string().min(1, {message: '入力して下さい。'}),
    ),
    internshipOffer: z
      .string()
      .optional()
      .refine((data) => data !== '', {
        message: '入力して下さい。',
      }),

    // P2のselection
    impressionImportance: z.optional(
      z.string().min(150, {message: '150文字以上で入力してください。'}),
    ),
    impressionQuestion: z.optional(
      z.string().min(1, {message: '入力してください。'}),
    ),
    impressionAttentionInterview: z.optional(
      z.string().min(150, {message: '150文字以上で入力してください。'}),
    ),
    impressionAttentionEachstep: z.optional(
      z.string().min(150, {message: '150文字以上で入力してください。'}),
    ),
    imressionResearch: z.optional(
      z.string().min(1, {message: '入力して下さい。'}),
    ),
    impressionImpression: z.optional(
      z.string().min(120, {message: '120文字以上で入力してください。'}),
    ),
    impressionAdvice: z.optional(
      z.string().min(200, {message: '200文字以上で入力してください'}),
    ),

    // P2のintern

    impressionInternImportance: z.optional(
      z.string().min(200, {message: '200文字以上で入力してください'}),
    ),
    impressionPrepare: z.optional(
      z.string().min(180, {message: '180文字以上で入力してください'}),
    ),
    impressionLearning: z.optional(
      z.string().min(300, {message: '300文字以上で入力してください'}),
    ),
    impressionExpectation: z.optional(
      z.string().min(200, {message: '200文字以上で入力してください'}),
    ),
    impressionInterchange: z.optional(
      z.string().min(35, {message: '35文字以上で入力してください'}),
    ),
    impressionAfterInterchange: z.optional(
      z.string().min(35, {message: '35文字以上で入力してください'}),
    ),
    impressionInternAdvice: z.optional(
      z.string().min(150, {message: '150文字以上で入力してください'}),
    ),
    obWhen: z.string().optional(),
    obHowmany: z.string().optional(),
    obQuestion: z.string().optional(),
    obUseful: z.string().optional(),
    // 内定後について
    afterWhen: z.string().optional(),
    afterHomeworkContent: z.string().optional(),
    afterRestriction: z.string().optional(),

    // 説明会
    conferencePeriod: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    conferenceTime: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    conferenceImpression: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    conferenceContent: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),

    // グループディスカッション
    gdTheme: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    gdTime: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    gdNumber: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    gdProceeding: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    gdContact: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),

    // エントリーシート
    esQuestion: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    esPoint: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    esContact: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),

    // その他の選考
    otherSchedule: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    otherHowlong: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    otherContent: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    otherCollege: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    otherReward: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    otherImression: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    otherContact: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),

    // 筆記試験
    paperExamForm: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    paperExamMeasure: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    paperExamContact: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),

    // **次面接
    interviewWorker: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    interviewStudent: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    interviewPlace: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    interviewTime: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    interviewProceeding: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    interviewQuestion: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    interviewAtmosphere: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    interviewImpression: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
    interviewContact: z
      .array(z.string().min(1, {message: '入力して下さい。'}))
      .optional(),
  })
  .refine(
    (data) => {
      if (data.hasPreference === '1') {
        return data.preferenceDetail && data.preferenceDetail !== '';
      }

      return true;
    },
    {
      message: '入力して下さい。',
      path: ['preferenceDetail'],
    },
  )
  .refine(
    (data) => {
      if (data.department === 'その他') {
        if (data.another) {
          return data.another !== '';
        }

        return false;
      }

      return true;
    },
    {
      message: '入力して下さい。',
      path: ['another'],
    },
  )
  .refine(
    (data) => {
      if (data.hasVisitedOb === '1') {
        return data.obWhen && data.obWhen !== '';
      }

      return true;
    },
    {
      message: '入力して下さい。',
      path: ['obWhen'],
    },
  )
  .refine(
    (data) => {
      if (data.hasVisitedOb === '1') {
        return data.obHowmany && data.obHowmany !== '';
      }

      return true;
    },
    {
      message: '入力して下さい。',
      path: ['obHowmany'],
    },
  )
  .refine(
    (data) => {
      if (data.hasVisitedOb === '1') {
        return data.obQuestion && data.obQuestion !== '';
      }

      return true;
    },
    {
      message: '入力して下さい。',
      path: ['obQuestion'],
    },
  )
  .refine(
    (data) => {
      if (data.hasVisitedOb === '1') {
        return data.obUseful && data.obUseful !== '';
      }

      return true;
    },
    {
      message: '入力して下さい。',
      path: ['obUseful'],
    },
  )
  .refine(
    (data) => {
      if (data.hasReceivedOffer === '1') {
        return data.afterWhen && data.afterWhen !== '';
      }

      return true;
    },
    {
      message: '入力して下さい。',
      path: ['afterWhen'],
    },
  )
  .refine(
    (data) => {
      if (data.hasReceivedOffer === '1') {
        return data.afterHomeworkContent && data.afterHomeworkContent !== '';
      }

      return true;
    },
    {
      message: '入力して下さい。',
      path: ['afterHomeworkContent'],
    },
  )
  .refine(
    (data) => {
      if (data.hasReceivedOffer === '1') {
        return data.afterRestriction && data.afterRestriction !== '';
      }

      return true;
    },
    {
      message: '入力して下さい。',
      path: ['afterRestriction'],
    },
  )
  .refine(
    (data) => {
      if (
        (data.conferencePeriod ?? []).length > 0 ||
        (data.gdTheme ?? []).length > 0 ||
        (data.esQuestion ?? []).length > 0 ||
        (data.otherSchedule ?? []).length > 0 ||
        (data.paperExamForm ?? []).length > 0 ||
        (data.interviewWorker ?? []).length > 0
      ) {
        return true;
      }

      return false;
    },
    {
      message: '選考ステップを1つ以上入力して下さい。',
      path: ['stepRequired'],
    },
  );

// GET,POST,PUT /3.0/selection-reports/:reportID/all-associated
export const ReportsEditData = z.object({
  reportId: z.number(),
});

export const ReportQuestionAnswerSchema = z.object({
  id: z.number(),
  reportInfoID: z.number(),
  reportQuestionQuestionID: z.number(),
  answer: z.string(),
});

export const ReportQuestionQuestionSchema = z.object({
  questionLabel: z.string(),
  placeholder: z.string(),
  example: z.string(),
  inputType: z.string(),
  note: z.string(),
  answerMinLength: z.number(),
  reportQuestionAnswer: ReportQuestionAnswerSchema,
});

export const ReportQuestionSchema = z.object({
  reportQuestionQuestions: z.array(ReportQuestionQuestionSchema),
  stepName: z.string(),
});

export const ReportStepQuestionSchema = z.object({
  id: z.number(),
  questionLabel: z.string(),
  placeholder: z.string(),
  example: z.string(),
  inputType: z.string(),
  note: z.string(),
  reportStepAnswer: z
    .object({
      id: z.number(),
      reportStepOrderID: z.number(),
      reportStepQuestionID: z.number(),
      answer: z.string(),
    })
    .optional(),
});

export const ReportStepSchema = z.object({
  id: z.number(),
  stepName: z.string(),
  reportStepQuestions: z.array(
    z.object({
      id: z.number(),
      questionLabel: z.string(),
      placeholder: z.string(),
      example: z.string(),
      inputType: z.string(),
      note: z.string(),
    }),
  ),
});

export const ReportStepOrderSchema = z.object({
  id: z.number(),
  reportInfoID: z.number(),
  reportStepID: z.enum([
    ReportStep.CONFERENCE,
    ReportStep.ES,
    ReportStep.PAPER,
    ReportStep.GD,
    ReportStep.INTERVIEW,
    ReportStep.OTHER,
  ]),
  stepOrder: z.number(),
  interviewOrder: z.number(),
  reportStepQuestions: z.array(ReportStepQuestionSchema),
});

export const DetailedReportInfoSchema = z.object({
  id: z.number(),
  reportType: z.string(),
  userID: z.number(),
  gradYear: z.number(),
  companyID: z.number(),
  companyName: z.string(),
  departmentID: z.number(),
  eventName: z.string(),
  freeOrPay: z.boolean(),
  getOffer: z.boolean(),
  status: z.string(),
});

export const ReportSchema = z.object({
  reportInfo: DetailedReportInfoSchema,
  reportQuestions: z.array(ReportQuestionSchema),
  reportSteps: z.array(ReportStepSchema),
  reportStepOrders: z.array(ReportStepOrderSchema).nullable(),
  reportNote: z.object({
    note: z.string(),
  }),
  canCreateES: z.boolean(),
});

export const ReportIDScheme = z.object({
  reportID: z.number(),
});

export const DepartmentParamScheme = z.object({
  id: z.number(),
  name: z.string(),
});

export const DepartmentScheme = z.object({
  departments: z.array(DepartmentParamScheme),
});

export const PutReportInfoSchema = z.object({
  id: z.number(),
  reportType: z.string(),
  userID: z.number(),
  gradYear: z.number(),
  companyID: z.number(),
  departmentID: z.number(),
  departmentName: z.string(),
  eventName: z.string(),
  freeOrPay: z.boolean(),
  getOffer: z.boolean(),
  status: z.string(),
});

export const PutReportAllAssociated = z.object({
  data: z.object({
    reportInfo: PutReportInfoSchema,
    reportQuestionAnswers: z.array(
      z.object({
        id: z.number(),
        reportInfoID: z.number(),
        reportQuestionQuestionID: z.number(),
        answer: z.string(),
      }),
    ),
    reportStepOrders: z.array(
      z.object({
        id: z.number(),
        reportInfoID: z.number(),
        reportStepID: z.number(),
        stepOrder: z.number(),
        interviewOrder: z.number(),
        reportStepAnswers: z.array(
          z.object({
            id: z.number(),
            reportStepOrderID: z.number(),
            reportStepQuestionID: z.number(),
            answer: z.string(),
          }),
        ),
      }),
    ),
    reportNote: z.string().optional(),
  }),
  page: z.number(),
  validate: z.boolean(),
});

export const PutReportAllAssociatedResponse = z.nullable(z.string());

// POST /3.0/entrysheets
export const PostEntrysheetInput = z.object({
  parentReportID: z.number().optional(),
  companyID: z.number().optional(),
  eventName: z.string().optional(),
  reportType: z.string().optional(),
});

export const PostEntrysheetOutput = z.object({
  entrysheetID: z.number(),
});

// GET /3.0/entrysheets/-/:entrysheetID
const Department = z.object({
  id: z.number(),
  name: z.string(),
});

const QuestionAnswer = z.object({
  id: z.number(),
  question: z.string(),
  answer: z.string(),
  remarks: z.string(),
  order: z.number(),
  deleted: z.boolean(),
});

const FixedQuestionAnswer = z.object({
  id: z.number(),
  questionID: z.number(),
  questionLabel: z.string(),
  answerID: z.number(),
  answerLabel: z.string(),
});

export const GetEntrysheetOutput = z.object({
  entrysheetID: z.number(),
  userID: z.number(),
  graduationYear: z.number(),
  status: z.string(),
  companyID: z.number(),
  companyName: z.string(),
  industryID: z.number(),
  selectionReportType: z.string(),
  note: z.string(),
  getOffer: z.boolean(),
  submissionDueDate: z.string(),
  department: Department,
  questionAnswers: z.array(QuestionAnswer),
  fixedQuestionAnswers: z.object({
    resultNotificationMethod: z.array(FixedQuestionAnswer),
    resultNotificationTime: z.array(FixedQuestionAnswer),
    submissionMethod: z.array(FixedQuestionAnswer),
  }),
});

export const GetEntrysheetInput = z.object({
  entrysheetID: z.number(),
});

// GET /3.0/entrysheet-questions
export const EntrysheetQuestionGroup = z.object({
  groupID: z.number(),
  items: z.array(
    z.object({
      id: z.number(),
      label: z.string(),
    }),
  ),
});

export const GetEntrysheetQuestionsOutput = z.object({
  questions: z.object({
    resultNotificationMethod: EntrysheetQuestionGroup,
    resultNotificationTime: EntrysheetQuestionGroup,
    submissionMethod: EntrysheetQuestionGroup,
  }),
});

// PUT /3.0/entrysheets/:entrysheetID
export const PutEntrysheetInput = z.object({
  ID: z.number(),
  questionAnswers: z
    .array(
      z.object({
        id: z.number(),
        question: z.string(),
        answer: z.string(),
        remarks: z.string(),
        order: z.number(),
      }),
    )
    .optional(),
  fixedQuestionAnswers: z
    .array(
      z.object({
        questionID: z.number(),
        answerID: z.number(),
      }),
    )
    .optional(),
  departmentID: z.number(),
  departmentName: z.string(),
  submissionDueDate: z.string(),
  selectionReportType: z.string(),
  getOffer: z.boolean(),
  page: z.number(),
  note: z.string().optional(),
  validate: z.boolean(),
});

export const PutEntrysheetOutput = z.nullable(z.string());

export const PutEntrysheetFormSchema = z
  .object({
    questionAnswers: z
      .array(
        z.object({
          id: z.number(),
          question: z.string().min(1, {message: '入力して下さい。'}),
          answer: z.string().min(1, {message: '入力して下さい。'}),
          remarks: z.string().min(1, {message: '入力して下さい。'}),
          order: z.number(),
        }),
      )
      .optional(),
    resultNotificationMethod: z
      .string()
      .optional()
      .refine((value) => value !== '', {message: '必須項目です'}),
    resultNotificationTime: z
      .string()
      .optional()
      .refine((value) => value !== '', {message: '必須項目です'}),
    submissionMethod: z
      .string()
      .optional()
      .refine((value) => value !== '', {message: '必須項目です'}),
    department: z
      .string()
      .refine((value) => value !== '0', {message: '必須項目です'}),
    otherDepartment: z.string().optional(),
    submissionDueDate: z.string().min(1, {message: '入力して下さい。'}),
    getOffer: z.string(),
  })
  .refine(
    (data) => {
      if (data.department === OTHER) {
        return data.otherDepartment !== '';
      }

      return true;
    },
    {
      message: '入力して下さい。',
      path: ['otherDepartment'],
    },
  );

export const EntrysheetResponse = z.object({
  MyEntrysheets: z.array(
    z.object({
      id: z.number(),
      companyName: z.string(),
      status: z.string(),
      type: z.string(),
    }),
  ),
  Page: z.number(),
  Limit: z.number(),
});

export const SelectionReportResponse = z.object({
  mySelectionReports: z.array(
    z.object({
      selectionReportID: z.number(),
      companyName: z.string(),
      selectionType: z.string(),
      status: z.string(),
      entrysheetID: z.number(),
      entrysheetStatus: z.string(),
      canCreateEntrysheet: z.boolean(),
    }),
  ),
  page: z.number(),
  limit: z.number(),
});
